/** biome-ignore-all lint/complexity/noStaticOnlyClass: <> */

import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { isArray, isEmpty, isNil, isObject, isString, omitBy } from "lodash-es";
import { twMerge } from "tailwind-merge";
import { getNotFoundErrorMessage, isNotFoundError } from "@/api/core/error-message";

dayjs.extend(customParseFormat);

class HelperManager {
  public static cn = (...inputs: ClassValue[]) => {
    return twMerge(clsx(inputs));
  };
  public static isValid = (param: unknown) => {
    if (
      param === false ||
      param === null ||
      param === undefined ||
      (param instanceof Object && Object.keys(param).length === 0) ||
      (Array.isArray(param) && param.length === 0) ||
      (typeof param === "string" && param.trim() === "") ||
      Number.isNaN(param)
    ) {
      return false;
    }

    return true;
  };

  public static isBreakAll = (text: string) => {
    if (!text) return false;
    const segments = text.split(" ");
    for (const segment of segments) {
      if (segment.length > 20) {
        return true;
      }
    }
    return false;
  };
  // Define constants
  public static EMPTY_VALUE = "_";

  /**
   * Parse date input from user
   * Handles various date formats and returns dayjs object
   */
  public static parseDateInput(input: string) {
    // Already handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      return dayjs(input, "YYYY-MM-DD");
    }

    // Handle DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(input)) {
      return dayjs(input, "DD-MM-YYYY");
    }

    // Handle DDMMYYYY format
    if (/^\d{8}$/.test(input)) {
      return dayjs(input, "DDMMYYYY");
    }

    // Handle DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(input)) {
      return dayjs(input, "DD/MM/YYYY");
    }

    // Handle DD.MM.YYYY format
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(input)) {
      return dayjs(input, "DD.MM.YYYY");
    }

    // Handle YYYYMMDD format
    if (/^\d{4}\d{2}\d{2}$/.test(input)) {
      return dayjs(input, "YYYYMMDD");
    }

    return null;
  }
  /**
   * Parse API error and return array of error messages
   * @param error - Error response from API
   * @returns Array of error messages
   */
  public static parseApiError(error: unknown): string[] {
    try {
      if (isNotFoundError(error)) {
        return [getNotFoundErrorMessage(error)];
      }

      // Check if error is an object with message property
      if (error && typeof error === "object" && "message" in error) {
        const errorObj = error as { message: string; statusCode?: number; status?: string };

        // Only try to parse as JSON if the message looks like JSON
        if (
          errorObj.message &&
          typeof errorObj.message === "string" &&
          (errorObj.message.trim().startsWith("{") || errorObj.message.trim().startsWith("["))
        ) {
          try {
            const parsedErrors = JSON.parse(errorObj.message);

            // If it's an array, format error messages
            if (Array.isArray(parsedErrors)) {
              return parsedErrors.map((err, index) => {
                if (err.path && Array.isArray(err.path) && err.path.length > 0 && err.message) {
                  return `${err.path[0]}: ${err.message}${index === parsedErrors.length - 1 ? "" : ".  "}`;
                }
                return err.message || "Unknown error";
              });
            }

            // If it's a single object with path and message
            if (parsedErrors && typeof parsedErrors === "object") {
              if (
                parsedErrors.path &&
                Array.isArray(parsedErrors.path) &&
                parsedErrors.path.length > 0 &&
                parsedErrors.message
              ) {
                return [`${parsedErrors.path[0]}: ${parsedErrors.message}`];
              }

              if ("message" in parsedErrors) {
                return [parsedErrors.message];
              }
            }
          } catch {
            // JSON parsing failed, but that's okay - just use the original message
            // Only log in development to avoid console spam
            if (process.env.NODE_ENV === "development") {
              console.debug("API error message is not valid JSON, using as-is:", errorObj.message);
            }
          }
        }

        // Return the original message (not JSON or parsing failed)
        if (errorObj.message) {
          return [errorObj.message];
        }
      }

      // Fallback for other cases
      if (error instanceof Error) {
        return [error.message];
      }

      return ["Unknown error occurred"];
    } catch (e) {
      console.error("Unexpected error in parseApiError:", e);
      return ["Unknown error occurred"];
    }
  }

  public static omitNullValues<T>(obj: T): T {
    if (isArray(obj)) {
      return obj.map((item) => HelperManager.omitNullValues(item)) as unknown as T;
    }
    if (obj && typeof obj === "object" && !isArray(obj)) {
      return omitBy(obj, (value) => {
        if (isNil(value)) return true;
        if (isArray(value) && isEmpty(value)) return true;
        if (isString(value) && isEmpty(value)) return true;
        if (isObject(value) && !isArray(value) && isEmpty(value)) return true;
        return false;
      }) as unknown as T;
    }
    return obj;
  }

  // biome-ignore lint/suspicious/noExplicitAny: <no needed>
  public static objectToQueryString(obj: Record<string, any>): string {
    return Object.entries(obj)
      .reduce((acc, [k, v]) => {
        if (HelperManager.isValid(v)) {
          acc.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        }
        return acc;
      }, [] as string[])
      .join("&");
  }

  public static openInNewTab(href: string) {
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

export default HelperManager;
